import { useEffect, useState } from 'react';
import { trpc } from '../../lib/trpc';
import type { VenueType, PrimaryAtmosphere, Amenity } from '@wineryxplorer/shared';

interface MapBounds {
  ne: { lat: number; lng: number };
  sw: { lat: number; lng: number };
}

interface SearchAreaButtonProps {
  bounds: MapBounds;
  filters: {
    venueTypes?: VenueType[];
    primaryAtmospheres?: PrimaryAtmosphere[];
    amenities?: Amenity[];
  };
  onCommit: () => void;
}

export const SearchAreaButton = ({ bounds, filters, onCommit }: SearchAreaButtonProps) => {
  // Debounce bounds so rapid panning doesn't spam the count query
  const [debouncedBounds, setDebouncedBounds] = useState(bounds);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBounds(bounds), 300);
    return () => clearTimeout(t);
  }, [bounds]);

  const { data, isPending } = trpc.venue.countInBounds.useQuery(
    { bounds: debouncedBounds, filters },
    { staleTime: 5_000 }
  );

  const count = data?.count;
  const disabled = isPending || count === 0;

  let label: string;
  if (isPending) label = 'Search this area...';
  else if (count === 0) label = 'No wineries in this area';
  else label = `Search this area · ${count} winer${count === 1 ? 'y' : 'ies'}`;

  return (
    <button
      type="button"
      onClick={onCommit}
      disabled={disabled}
      className="px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-lg shadow-md hover:bg-primary-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
    >
      {label}
    </button>
  );
};
